import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useOnboarding } from "@/context/OnboardingContext";
import { useLang } from "@/context/LanguageContext";
import LanguagePill from "@/components/LanguagePill";
import OnboardingExit from "@/components/OnboardingExit";

export default function OnboardingProfile() {
  const navigate = useNavigate();
  const { data, update } = useOnboarding();
  const { t } = useLang();

  const GENDERS = [
    { key: "Male", label: t("ob.profile.gender.male") },
    { key: "Female", label: t("ob.profile.gender.female") },
    { key: "Prefer not to say", label: t("ob.profile.gender.nb") },
  ];
  const MARITAL = [
    { key: "Single", label: t("ob.profile.marital.single") },
    { key: "Married", label: t("ob.profile.marital.married") },
    { key: "Divorced", label: t("ob.profile.marital.divorced") },
    { key: "Widowed", label: t("ob.profile.marital.widowed") },
    { key: "Separated", label: t("ob.profile.marital.separated") },
  ];

  const [name, setName] = useState(data.profile?.name || "");
  const [age, setAge] = useState(data.profile?.age || "");
  const [gender, setGender] = useState(data.profile?.gender || "");
  const [marital, setMarital] = useState(data.profile?.marital_status || "");
  const [hasChildren, setHasChildren] = useState(data.profile?.has_children || false);
  const [childrenCount, setChildrenCount] = useState(data.profile?.children_count || 1);
  const [city, setCity] = useState(data.profile?.city || "");
  const [profession, setProfession] = useState(data.profile?.profession || "");

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        if (!name && me.data?.name) setName(me.data.name);
      } catch {}
    })();
  // eslint-disable-next-line
  }, []);

  const canContinue = name && age && gender && marital && city;

  const next = () => {
    update({
      profile: {
        name, age: Number(age), gender, marital_status: marital,
        has_children: hasChildren, children_count: hasChildren ? Number(childrenCount) : 0,
        children_ages: [], city, profession,
      },
    });
    navigate("/onboarding/priorities");
  };

  return (
    <div className="min-h-screen px-6 py-10 max-w-md mx-auto" style={{ backgroundColor: "var(--vedari-cream)", color: "var(--vedari-ink)" }}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <Step n={1} total={4} />
        <LanguagePill persist className="ml-3" />
        <OnboardingExit />
      </div>
      <h1 className="font-display text-4xl mt-3 mb-2 leading-tight" data-testid="onboarding-profile-title">
        {t("ob.profile.title")}
      </h1>
      <p className="text-body" style={{ color: "var(--vedari-ink-soft)" }}>
        {t("ob.profile.sub")}
      </p>

      <div className="space-y-5 mt-8">
        <Field label={t("ob.profile.name")}>
          <input data-testid="profile-name" className="vedari-input" value={name} onChange={e => setName(e.target.value)} placeholder={t("ob.profile.name_ph")} />
        </Field>
        <Field label={t("ob.profile.age")}>
          <input data-testid="profile-age" type="number" min={18} max={80} className="vedari-input" value={age} onChange={e => setAge(e.target.value)} placeholder={t("ob.profile.age_ph")} />
        </Field>
        <Field label={t("ob.profile.gender")}>
          <Radios value={gender} onChange={setGender} options={GENDERS} testid="profile-gender" />
        </Field>
        <Field label={t("ob.profile.marital")}>
          <Radios value={marital} onChange={setMarital} options={MARITAL} testid="profile-marital" />
        </Field>
        <Field label={t("ob.profile.children")}>
          <label className="flex items-center gap-3 text-body" data-testid="profile-children-toggle">
            <input type="checkbox" checked={hasChildren} onChange={e => setHasChildren(e.target.checked)}
              className="w-5 h-5 accent-[var(--vedari-gold)]" />
            <span>{t("ob.profile.children_yes")}</span>
          </label>
          {hasChildren && (
            <input type="number" min={1} max={6} className="vedari-input mt-3" value={childrenCount} onChange={e => setChildrenCount(e.target.value)} placeholder={t("ob.profile.children_count_ph")} data-testid="profile-children-count" />
          )}
        </Field>
        <Field label={t("ob.profile.city")}>
          <input data-testid="profile-city" className="vedari-input" value={city} onChange={e => setCity(e.target.value)} placeholder={t("ob.profile.city_ph")} />
        </Field>
        <Field label={t("ob.profile.profession")}>
          <input data-testid="profile-profession" className="vedari-input" value={profession} onChange={e => setProfession(e.target.value)} placeholder={t("ob.profile.profession_ph")} />
        </Field>
      </div>

      <button data-testid="onboarding-continue-btn" onClick={next} disabled={!canContinue} className="vedari-btn-primary w-full mt-10">
        {t("common.continue")}
      </button>
    </div>
  );
}

function Step({ n, total }) {
  return (
    <div className="flex items-center gap-2 flex-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className="h-[2px] flex-1 rounded-full" style={{ backgroundColor: i < 1 ? "var(--vedari-gold)" : "var(--vedari-divider)" }} />
      ))}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-body-sm font-medium mb-2" style={{ color: "var(--vedari-ink-soft)" }}>{label}</label>
      {children}
    </div>
  );
}
function Radios({ value, onChange, options, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map(o => (
        <button key={o.key} type="button" onClick={() => onChange(o.key)}
          data-testid={`${testid}-${o.key.toLowerCase().replace(/\s+/g, "-")}`}
          className="px-4 py-2 rounded-full text-body-sm transition-all"
          style={{
            backgroundColor: value === o.key ? "var(--vedari-gold)" : "transparent",
            color: value === o.key ? "var(--vedari-cream)" : "var(--vedari-ink)",
            border: `1.5px solid ${value === o.key ? "var(--vedari-gold)" : "var(--vedari-divider)"}`,
          }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
